import { useCallback, useEffect, useState } from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import { useForm } from 'react-hook-form'
import { getNodes, useError, withParams } from 'resources'
import { pipe, map } from 'ramda'
import { useHistory } from 'react-router'
import { CUSTOM_SINGLE_SIMULATED, ENEM_SINGLE_SIMULATED } from 'routes'
import { useMockExamsPlatformMutation } from 'resources/mock-exams-platform/use-mock-exams-platform-mutation'

export const useCreateMockExam = () => {
  const [showNewSimulatedModal, setShowNewSimulatedModal] = useState(false)
  const [simulatedTypePlaylist, setSimulatedTypePlaylist] = useState(false)
  const [simulatedTypeEnem, setSimulatedTypeEnem] = useState(false)
  const [discipline, setDiscipline] = useState()
  const [enemKnowledgeAreaID, setEnemKnowledgeAreaId] = useState()
  const [allSubjects, setAllSubjects] = useState()
  const [subjects, setSubjects] = useState([])
  const [status, setStatus] = useState(false)
  const [errors, setErrors] = useState({})
  const history = useHistory()

  const { loading: isCreatingEnemSimulated, createUserEnemSimulated } = useCreateUserENEMSimulated({
    history,
    enemKnowledgeAreaID,
  })

  const { loading: isCreatingAPlaylistSimulated, createUserCustomSimulated } = useCreateUserCustomSimulated()

  const openModalNewSimulated = () => {
    setShowNewSimulatedModal(true)
  }
  const {
    handleSubmit,
  } = useForm()

  const onSubmit = async () => {
    if (!simulatedTypePlaylist && !simulatedTypeEnem) {
      setErrors({ simulateType: { type: 'required' } })
      return
    }

    if (simulatedTypePlaylist) {
      if (!discipline) {
        setErrors({ discipline: { type: 'required' } })
        return
      }
      if (!subjects.length === 0) {
        setErrors({ subjects: { type: 'required' } })
        return
      }
    }

    if (simulatedTypeEnem) {
      if (!enemKnowledgeAreaID) {
        setErrors({ discipline: { type: 'required' } })
        return
      }
    }

    try {
      if (simulatedTypePlaylist) {
        return createUserCustomSimulated({ history, discipline, subjects })
      }

      if (simulatedTypeEnem) {
        createUserEnemSimulated({ history, enemKnowledgeAreaID })
        return
      }
    } catch (err) {
      setStatus(true)
      setErrors(err)
    }
  }

  const onClose = () => {
    setShowNewSimulatedModal(false)
    setSimulatedTypeEnem(false)
    setSimulatedTypePlaylist(false)
  }

  const onChangeSimulated = formField => ({ currentTarget }) => {
    const [, value] = formField.cata({
      Toggle: () => [currentTarget.name, currentTarget.checked],
      Select: () => [currentTarget.name, currentTarget.value],
    })
    if (value === 'PLAYLIST') {
      setEnemKnowledgeAreaId(null)
      setSimulatedTypePlaylist(true)
      setSimulatedTypeEnem(false)
    }

    if (value === 'ENEM') {
      setDiscipline(null)
      setSimulatedTypePlaylist(false)
      setSimulatedTypeEnem(true)
    }

    setErrors({})
    setAllSubjects()
  }

  const handleSubjects = useCallback(async subjects => {
    setAllSubjects(toOptions(subjects))
  }, [])

  const [getSubjects] = useLazyQuery(SUBJECTS_QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      filter: {
        disciplineId: discipline,
        onlyParents: true,
      },
    },
    onCompleted: handleSubjects,
  })

  const onChangeDiscipline = (e) => {
    setDiscipline(e.target.value)
    getSubjects({ discipline })
    setStatus(false)
    setErrors({})
  }

  const onChangeDisciplineEnem = (e) => {
    setEnemKnowledgeAreaId(e.target.value)
    setStatus(false)
    setErrors({})
  }

  const onChangeSubject = (newSubjects) => {
    setSubjects([newSubjects.currentTarget.value])
    setErrors({})
  }

  const createNewSimulatedFromGenericList = async (row) => {
    if (!row.discipline || !row.subject) {
      return
    }

    const discipline = Array.isArray(row.discipline) ? row.discipline[0].id : row.discipline.id
    const subjects = Array.isArray(row.subject) ? row.subject[0].id : [row.subject.id]

    await createUserCustomSimulated({
      history,
      discipline,
      subjects,
    })
  }

  return {
    createNewSimulatedFromGenericList,
    showNewSimulatedModal,
    openModalNewSimulated,
    onClose,
    onChangeSimulated,
    onChangeDisciplineEnem,
    simulatedTypePlaylist,
    simulatedTypeEnem,
    onChangeDiscipline,
    discipline,
    onChangeSubject,
    onSubmit,
    handleSubmit,
    allSubjects,
    status,
    errors,
    isStarting: isCreatingAPlaylistSimulated || isCreatingEnemSimulated,
  }
}

function useCreateUserENEMSimulated () {
  const [mutate, { loading, error }] = useMockExamsPlatformMutation(CREATE_USER_MOCK_EXAM)

  const [handleError] = useError()

  const createUserEnemSimulated = useCallback(async ({ history, enemKnowledgeAreaID }) => {
    const { data: { createUserEnemMockExam } = {} } = await mutate({
      variables: {
        knowledgeAreaId: enemKnowledgeAreaID,
      },
    })

    history.push(withParams(ENEM_SINGLE_SIMULATED, {
      userMockExamId: createUserEnemMockExam._id,
    }))
  }, [mutate])

  useEffect(() => {
    if (error) {
      handleError(error)
    }
  }, [error, handleError])

  return {
    loading,
    createUserEnemSimulated,
  }
}

function useCreateUserCustomSimulated () {
  const [mutate, { loading, error }] = useMockExamsPlatformMutation(CREATE_CUSTOM_USER_MOCK_EXAM)
  const [handleError] = useError()

  const createUserCustomSimulated = useCallback(async ({ history, discipline, subjects }) => {
    const { data: { createUserCustomMockExam } = {} } = await mutate({
      variables: {
        disciplines: [discipline],
        subjects: subjects,
      },
    })

    history.push(withParams(CUSTOM_SINGLE_SIMULATED, {
      userMockExamId: createUserCustomMockExam._id,
    }))
  }, [mutate])

  useEffect(() => {
    if (error) {
      handleError(error)
    }
  }, [error, handleError])

  return {
    loading,
    createUserCustomSimulated,
  }
}

export const CREATE_USER_SIMULATED = gql`
mutation ($input: SimulatedInput!) {
  createUserSimulated (input: $input) {
    meta{
    slug
    }
  }
}
`

const CREATE_USER_MOCK_EXAM = gql`
mutation CreateUserMockExam($knowledgeAreaId: String) {
    createUserEnemMockExam(
        knowledgeAreaId: $knowledgeAreaId
    ) {
        _id
        endedAt
        mockExamId
        startedAt
    }
}
`

const CREATE_CUSTOM_USER_MOCK_EXAM = gql`
mutation CreateUserCustomMockExam($disciplines: [String!]!, $subjects: [String!]!) {
    createUserCustomMockExam(
        disciplines: $disciplines
        subjects: $subjects
    ) {
      _id
    }
}
`

const SUBJECTS_QUERY = gql`
  query GetSubjects($filter:FilterSubjectsInput!){
    subjects (filter: $filter){
     nodes {
       id
       slug
       name
      }
    }
  }
`

const toOption = ({
  name: label,
  id,
}) => ({ label, value: id, id })

const toOptions = pipe(
  getNodes('subjects'),
  map(toOption)
)
